import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { ResponsiveLayout } from '@/components/ResponsiveLayout';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { User, Target, TrendingUp, Users, Calendar, ChevronLeft, ChevronRight, Edit, Save, X } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Month names in Malay
const MONTHS = ['Januari', 'Februari', 'Mac', 'April', 'Mei', 'Jun', 'Julai', 'Ogos', 'September', 'Oktober', 'November', 'Disember'];

export default function ChannelProfilePage() {
  const [ibs, setIbs] = useState([]);
  const [selectedIB, setSelectedIB] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  
  // Month navigation state
  const currentDate = new Date();
  const [selectedMonth, setSelectedMonth] = useState(currentDate.getMonth());
  const [selectedYear, setSelectedYear] = useState(currentDate.getFullYear());
  
  // Edit mode states
  const [editingYearly, setEditingYearly] = useState(false);
  const [editingMonthly, setEditingMonthly] = useState(false);
  
  // Yearly targets (same for whole year)
  const [yearlyTargets, setYearlyTargets] = useState({
    commission: 50000,
    deposit: 500000,
    lot: 5000,
    active_client: 500
  });
  
  // Monthly targets (different per month) - stored as object with month-year keys
  const [monthlyTargetsData, setMonthlyTargetsData] = useState({});
  
  // Get current month's targets
  const getMonthKey = (month, year) => `${year}-${String(month + 1).padStart(2, '0')}`;
  
  const getCurrentMonthTargets = () => {
    const key = getMonthKey(selectedMonth, selectedYear);
    return monthlyTargetsData[key] || {
      commission: selectedIB?.monthly_target_commission || 5000,
      deposit: selectedIB?.monthly_target_deposit || 50000,
      lot: selectedIB?.monthly_target_lot || 500,
      active_client: selectedIB?.monthly_target_active_client || 50
    };
  };
  
  const [editMonthlyValues, setEditMonthlyValues] = useState({});
  const [editYearlyValues, setEditYearlyValues] = useState({});

  useEffect(() => {
    fetchIBs();
    
    const handleIBChange = () => {
      const savedIB = localStorage.getItem('selectedIB');
      if (savedIB && ibs.length > 0) {
        const ib = ibs.find(i => i.id === savedIB);
        if (ib) setSelectedIB(ib);
      }
    };

    window.addEventListener('ibChanged', handleIBChange);
    return () => window.removeEventListener('ibChanged', handleIBChange);
  }, [ibs]);

  // Load saved targets from MongoDB when IB changes
  useEffect(() => {
    if (selectedIB) {
      const fetchTargets = async () => {
        try {
          const token = localStorage.getItem('token');
          const res = await axios.get(`${API}/ib-targets/${selectedIB.id}`, { headers: { Authorization: `Bearer ${token}` } });
          const saved = res.data?.data;
          if (saved) {
            const yearKey = String(selectedYear);
            if (saved.yearly_targets && saved.yearly_targets[yearKey]) {
              setYearlyTargets(saved.yearly_targets[yearKey]);
            } else {
              setYearlyTargets({
                commission: (selectedIB.monthly_target_commission || 5000) * 12,
                deposit: (selectedIB.monthly_target_deposit || 50000) * 12,
                lot: (selectedIB.monthly_target_lot || 500) * 12,
                active_client: (selectedIB.monthly_target_active_client || 50) * 12
              });
            }
            if (saved.monthly_targets) {
              setMonthlyTargetsData(saved.monthly_targets);
            }
          } else {
            setYearlyTargets({
              commission: (selectedIB.monthly_target_commission || 5000) * 12,
              deposit: (selectedIB.monthly_target_deposit || 50000) * 12,
              lot: (selectedIB.monthly_target_lot || 500) * 12,
              active_client: (selectedIB.monthly_target_active_client || 50) * 12
            });
            setMonthlyTargetsData({});
          }
        } catch {
          setYearlyTargets({
            commission: (selectedIB.monthly_target_commission || 5000) * 12,
            deposit: (selectedIB.monthly_target_deposit || 50000) * 12,
            lot: (selectedIB.monthly_target_lot || 500) * 12,
            active_client: (selectedIB.monthly_target_active_client || 50) * 12
          });
        }
      };
      fetchTargets();
    }
  }, [selectedIB, selectedYear]);

  const fetchIBs = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }

      const response = await axios.get(`${API}/ib/list`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setIbs(response.data);
      
      const savedIB = localStorage.getItem('selectedIB');
      if (savedIB) {
        const ib = response.data.find(i => i.id === savedIB);
        if (ib) {
          setSelectedIB(ib);
        } else if (response.data.length > 0) {
          setSelectedIB(response.data[0]);
        }
      } else if (response.data.length > 0) {
        setSelectedIB(response.data[0]);
        localStorage.setItem('selectedIB', response.data[0].id);
      }
    } catch (error) {
      toast.error('Failed to load IBs');
    } finally {
      setLoading(false);
    }
  };

  // Month navigation handlers
  const goToPreviousMonth = () => {
    if (selectedMonth === 0) {
      setSelectedMonth(11);
      setSelectedYear(selectedYear - 1);
    } else {
      setSelectedMonth(selectedMonth - 1);
    }
  };

  const goToNextMonth = () => {
    if (selectedMonth === 11) {
      setSelectedMonth(0);
      setSelectedYear(selectedYear + 1);
    } else {
      setSelectedMonth(selectedMonth + 1);
    }
  };

  // Save handlers
  const saveTargetsToMongo = async (newYearly, newMonthly) => {
    try {
      const token = localStorage.getItem('token');
      const yearKey = String(selectedYear);
      // Get existing data first
      const res = await axios.get(`${API}/ib-targets/${selectedIB.id}`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: { data: null } }));
      const existing = res.data?.data || {};
      
      await axios.put(`${API}/ib-targets/${selectedIB.id}`, {
        yearly_targets: { ...(existing.yearly_targets || {}), [yearKey]: newYearly || yearlyTargets },
        monthly_targets: newMonthly || monthlyTargetsData
      }, { headers: { Authorization: `Bearer ${token}` } });
    } catch { toast.error('Failed to save targets'); }
  };

  const handleSaveYearly = async () => {
    setYearlyTargets(editYearlyValues);
    setEditingYearly(false);
    await saveTargetsToMongo(editYearlyValues, null);
    toast.success('Yearly targets disimpan!');
  };

  const handleSaveMonthly = async () => {
    const key = getMonthKey(selectedMonth, selectedYear);
    const newData = { ...monthlyTargetsData, [key]: editMonthlyValues };
    setMonthlyTargetsData(newData);
    setEditingMonthly(false);
    await saveTargetsToMongo(null, newData);
    toast.success(`Target ${MONTHS[selectedMonth]} ${selectedYear} disimpan!`);
  };

  const startEditYearly = () => {
    setEditYearlyValues({ ...yearlyTargets });
    setEditingYearly(true);
  };

  const startEditMonthly = () => {
    setEditMonthlyValues({ ...getCurrentMonthTargets() });
    setEditingMonthly(true);
  };

  if (loading) {
    return (
      <ResponsiveLayout><div className="flex-1 min-h-screen bg-[#0A0A0B]">
        <main className="flex-1 flex items-center justify-center">
          <p className="text-[#ADB5BD]">Loading...</p>
        </main>
      </div></ResponsiveLayout>
    );
  }

  const currentMonthTargets = getCurrentMonthTargets();

  return (
    <ResponsiveLayout><div className="flex-1 min-h-screen bg-[#0A0A0B] text-[#F8F9FA]">
      
      <main className="flex-1 px-4 md:px-6 lg:px-8 py-6 md:py-8 overflow-y-auto">
        <div className="mb-6">
          <h1 className="text-4xl font-bold font-heading text-[#F8F9FA] mb-2" data-testid="page-title">
            Channel Profile
          </h1>
          <p className="text-[#ADB5BD]">IB Identity & Context</p>
        </div>

        <div className="mb-6">
          <label className="text-xs uppercase tracking-wider text-[#ADB5BD] mb-2 block">Select IB</label>
          <Select value={selectedIB?.id} onValueChange={(value) => { setSelectedIB(ibs.find(ib => ib.id === value)); localStorage.setItem('selectedIB', value); window.dispatchEvent(new Event('ibChanged')); }}>
            <SelectTrigger className="w-64 bg-[#1C1C1F] border-[#2A2A2E] text-[#F8F9FA]" data-testid="ib-selector">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#1C1C1F] border-[#2A2A2E] max-h-[240px] overflow-y-auto">
              {ibs.map(ib => (
                <SelectItem key={ib.id} value={ib.id} className="text-[#F8F9FA]">{ib.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {selectedIB && (
          <div className="space-y-6">
            {/* IB Biography */}
            <div className="border border-[#2A2A2E] bg-[#141416] rounded-xl p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#E63946] to-[#DC2626] flex items-center justify-center">
                  <User className="text-white" size={20} />
                </div>
                <h2 className="text-xl font-semibold font-heading">IB Biography</h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <p className="text-xs text-[#6B7280] uppercase tracking-wider mb-1">IB Name</p>
                  <p className="text-lg text-[#F8F9FA] font-medium">{selectedIB.name}</p>
                </div>
                <div>
                  <p className="text-xs text-[#6B7280] uppercase tracking-wider mb-1">Status</p>
                  <Badge className={selectedIB.status === 'active' ? 'bg-[#10B981]/20 text-[#10B981]' : 'bg-[#6B7280]/20 text-[#6B7280]'}>
                    {selectedIB.status}
                  </Badge>
                </div>
                <div>
                  <p className="text-xs text-[#6B7280] uppercase tracking-wider mb-1">Experience & Positioning</p>
                  <p className="text-sm text-[#9CA3AF]">Professional IB with focus on forex trading education and signal provision.</p>
                </div>
              </div>
            </div>

            {/* YEARLY TARGETS - NEW SECTION */}
            <div className="border border-[#2A2A2E] bg-[#141416] rounded-xl p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#8B5CF6] to-[#7C3AED] flex items-center justify-center">
                    <Calendar className="text-white" size={20} />
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold font-heading">Yearly Targets</h2>
                    <p className="text-xs text-[#6B7280]">Target tahunan {selectedYear}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => setSelectedYear(selectedYear - 1)}
                    className="h-8 w-8 p-0 border-[#2A2A2E]"
                  >
                    <ChevronLeft size={16} />
                  </Button>
                  <span className="text-lg font-bold text-white px-3">{selectedYear}</span>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => setSelectedYear(selectedYear + 1)}
                    className="h-8 w-8 p-0 border-[#2A2A2E]"
                  >
                    <ChevronRight size={16} />
                  </Button>
                  {!editingYearly ? (
                    <Button variant="outline" size="sm" onClick={startEditYearly} className="ml-2 border-[#2A2A2E] text-[#9CA3AF]">
                      <Edit size={14} className="mr-1" /> Edit
                    </Button>
                  ) : (
                    <div className="flex gap-1 ml-2">
                      <Button size="sm" onClick={handleSaveYearly} className="bg-[#10B981] hover:bg-[#059669] text-white">
                        <Save size={14} className="mr-1" /> Simpan
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => setEditingYearly(false)} className="border-[#2A2A2E]">
                        <X size={14} />
                      </Button>
                    </div>
                  )}
                </div>
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                  { key: 'commission', label: 'Commission', prefix: 'RM ', color: '#10B981' },
                  { key: 'deposit', label: 'Deposit', prefix: '$ ', color: '#3B82F6' },
                  { key: 'lot', label: 'Traded Lot', prefix: '', color: '#F59E0B' },
                  { key: 'active_client', label: 'Active Client', prefix: '', color: '#8B5CF6' },
                ].map((item) => (
                  <div key={item.key} className="p-4 bg-[#1C1C1F] rounded-xl border border-[#2A2A2E]">
                    <p className="text-xs text-[#6B7280] uppercase tracking-wider mb-2">{item.label}</p>
                    {editingYearly ? (
                      <Input
                        type="number"
                        value={editYearlyValues[item.key] || 0}
                        onChange={(e) => setEditYearlyValues({...editYearlyValues, [item.key]: parseFloat(e.target.value)})}
                        className="bg-[#141416] border-[#2A2A2E] text-white text-xl font-mono"
                      />
                    ) : (
                      <p className="text-2xl font-mono font-bold" style={{ color: item.color }}>
                        {item.prefix}{yearlyTargets[item.key]?.toLocaleString() || 0}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* MONTHLY TARGETS - WITH MONTH NAVIGATION */}
            <div className="border border-[#2A2A2E] bg-[#141416] rounded-xl p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#F59E0B] to-[#F97316] flex items-center justify-center">
                    <Target className="text-white" size={20} />
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold font-heading">Monthly Targets</h2>
                    <p className="text-xs text-[#6B7280]">Target berbeza setiap bulan</p>
                  </div>
                </div>
                
                {/* Month Navigation */}
                <div className="flex items-center gap-2">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={goToPreviousMonth}
                    className="h-8 w-8 p-0 border-[#2A2A2E] hover:bg-[#E63946]/10"
                    data-testid="prev-month-btn"
                  >
                    <ChevronLeft size={16} />
                  </Button>
                  <div className="min-w-[160px] text-center">
                    <span className="text-lg font-bold text-white">{MONTHS[selectedMonth]}</span>
                    <span className="text-lg text-[#6B7280] ml-2">{selectedYear}</span>
                  </div>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={goToNextMonth}
                    className="h-8 w-8 p-0 border-[#2A2A2E] hover:bg-[#E63946]/10"
                    data-testid="next-month-btn"
                  >
                    <ChevronRight size={16} />
                  </Button>
                  
                  {!editingMonthly ? (
                    <Button variant="outline" size="sm" onClick={startEditMonthly} className="ml-2 border-[#2A2A2E] text-[#9CA3AF]">
                      <Edit size={14} className="mr-1" /> Edit
                    </Button>
                  ) : (
                    <div className="flex gap-1 ml-2">
                      <Button size="sm" onClick={handleSaveMonthly} className="bg-[#10B981] hover:bg-[#059669] text-white">
                        <Save size={14} className="mr-1" /> Simpan
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => setEditingMonthly(false)} className="border-[#2A2A2E]">
                        <X size={14} />
                      </Button>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Progress vs Yearly */}
              <div className="mb-4 p-3 bg-[#1C1C1F] rounded-lg">
                <div className="flex items-center justify-between text-xs text-[#6B7280] mb-1">
                  <span>Progress vs Yearly Target</span>
                  <span>{((currentMonthTargets.commission / yearlyTargets.commission) * 100 * 12).toFixed(1)}% projected</span>
                </div>
                <div className="h-2 bg-[#2A2A2E] rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-gradient-to-r from-[#E63946] to-[#F97316] rounded-full transition-all"
                    style={{ width: `${Math.min(((selectedMonth + 1) / 12) * 100, 100)}%` }}
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                  { key: 'commission', label: 'Commission', prefix: 'RM ', color: '#10B981' },
                  { key: 'deposit', label: 'Deposit', prefix: '$ ', color: '#3B82F6' },
                  { key: 'lot', label: 'Traded Lot', prefix: '', color: '#F59E0B' },
                  { key: 'active_client', label: 'Active Client', prefix: '', color: '#8B5CF6' },
                ].map((item) => (
                  <div key={item.key} className="p-4 bg-[#1C1C1F] rounded-xl border border-[#2A2A2E]">
                    <p className="text-xs text-[#6B7280] uppercase tracking-wider mb-2">{item.label}</p>
                    {editingMonthly ? (
                      <Input
                        type="number"
                        value={editMonthlyValues[item.key] || 0}
                        onChange={(e) => setEditMonthlyValues({...editMonthlyValues, [item.key]: parseFloat(e.target.value)})}
                        className="bg-[#141416] border-[#2A2A2E] text-white text-xl font-mono"
                      />
                    ) : (
                      <>
                        <p className="text-2xl font-mono font-bold" style={{ color: item.color }}>
                          {item.prefix}{currentMonthTargets[item.key]?.toLocaleString() || 0}
                        </p>
                        <p className="text-[10px] text-[#6B7280] mt-1">
                          {((currentMonthTargets[item.key] / yearlyTargets[item.key]) * 100).toFixed(1)}% of yearly
                        </p>
                      </>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Biggest Win */}
            <div className="border border-[#2A2A2E] bg-[#141416] rounded-xl p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#10B981] to-[#059669] flex items-center justify-center">
                  <TrendingUp className="text-white" size={20} />
                </div>
                <h2 className="text-xl font-semibold font-heading">Biggest Win</h2>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div className="p-4 bg-[#1C1C1F] rounded-xl border border-[#2A2A2E]">
                  <p className="text-xs text-[#6B7280] uppercase tracking-wider mb-2">Highest Commission (Day)</p>
                  <p className="text-2xl font-mono font-bold text-[#10B981]">RM 350.50</p>
                  <p className="text-xs text-[#6B7280] mt-1">15 Jan 2025</p>
                </div>
                <div className="p-4 bg-[#1C1C1F] rounded-xl border border-[#2A2A2E]">
                  <p className="text-xs text-[#6B7280] uppercase tracking-wider mb-2">Highest Deposit (Day)</p>
                  <p className="text-2xl font-mono font-bold text-[#3B82F6]">RM 8,500</p>
                  <p className="text-xs text-[#6B7280] mt-1">20 Jan 2025</p>
                </div>
                <div className="p-4 bg-[#1C1C1F] rounded-xl border border-[#2A2A2E]">
                  <p className="text-xs text-[#6B7280] uppercase tracking-wider mb-2">Most Active Clients (Day)</p>
                  <p className="text-2xl font-mono font-bold text-[#8B5CF6]">60</p>
                  <p className="text-xs text-[#6B7280] mt-1">18 Jan 2025</p>
                </div>
              </div>
            </div>

            {/* Team Members */}
            <div className="border border-[#2A2A2E] bg-[#141416] rounded-xl p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#F59E0B] to-[#D97706] flex items-center justify-center">
                  <Users className="text-white" size={20} />
                </div>
                <h2 className="text-xl font-semibold font-heading">Team Members</h2>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                <div className="p-4 bg-[#1C1C1F] rounded-xl border border-[#2A2A2E]">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm font-medium text-[#F8F9FA]">Marketing Team</p>
                    <Badge className="bg-[#3B82F6]/20 text-[#3B82F6]">3</Badge>
                  </div>
                  <p className="text-xs text-[#6B7280]">Content & Ads Management</p>
                </div>
                <div className="p-4 bg-[#1C1C1F] rounded-xl border border-[#2A2A2E]">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm font-medium text-[#F8F9FA]">Sales Team</p>
                    <Badge className="bg-[#10B981]/20 text-[#10B981]">5</Badge>
                  </div>
                  <p className="text-xs text-[#6B7280]">Client Acquisition & Retention</p>
                </div>
                <div className="p-4 bg-[#1C1C1F] rounded-xl border border-[#2A2A2E]">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm font-medium text-[#F8F9FA]">Operation Team</p>
                    <Badge className="bg-[#F59E0B]/20 text-[#F59E0B]">2</Badge>
                  </div>
                  <p className="text-xs text-[#6B7280]">Trading Signals & Analysis</p>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
    </ResponsiveLayout>
  );
}
